import { useEffect, useMemo, useState } from "react";
import {
  SOCIAL_PLATFORM_ICONS,
  SOCIAL_PLATFORM_OPTIONS,
  resolveSocialIconSrc,
} from "./socialPlatforms";
import MediaUploadBox from "../media/MediaUploadBox";
import { resolveMediaUrl } from "../../../utils/mediaUrl";

export default function SocialFormModal({
  open,
  initial,
  busy,
  uploadBusy,
  onClose,
  onSubmit,
  onUploadIcon,
}) {
  const [platform, setPlatform] = useState("twitter");
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [icon, setIcon] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setPlatform(initial?.platform || "twitter");
    setLabel(initial?.label || "");
    setUrl(initial?.url || "");
    setIcon(initial?.icon || "");
    setError("");
  }, [open, initial]);

  const previewSrc = useMemo(
    () =>
      resolveSocialIconSrc({
        platform,
        icon,
      }),
    [platform, icon]
  );

  if (!open) return null;

  function handlePlatformChange(next) {
    setPlatform(next);
    const option = SOCIAL_PLATFORM_OPTIONS.find((item) => item.value === next);
    if (!label.trim() || SOCIAL_PLATFORM_OPTIONS.some((item) => item.label === label)) {
      setLabel(option?.label || next);
    }
    // Switching preset clears custom uploaded icon so the new platform icon shows.
    if (next !== "custom") {
      setIcon("");
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!platform) {
      setError("Please choose a platform or icon.");
      return;
    }
    if (!url.trim()) {
      setError("Please enter a URL.");
      return;
    }
    if (platform === "custom" && !icon.trim()) {
      setError("Please upload a custom icon.");
      return;
    }

    const option = SOCIAL_PLATFORM_OPTIONS.find((item) => item.value === platform);
    onSubmit({
      platform,
      label: label.trim() || option?.label || platform,
      url: url.trim(),
      icon: icon.trim(),
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-slate-900/40"
        onClick={busy || uploadBusy ? undefined : onClose}
      />
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-slate-900">
          {initial ? "Edit social link" : "Add social link"}
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Add or update the link, and choose or upload the icon shown in the footer.
        </p>

        <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
          {error ? (
            <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          ) : null}

          <div>
            <p className="text-sm font-medium text-slate-700">Icon</p>
            <div className="mt-2 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white ring-1 ring-slate-200">
                <img
                  src={previewSrc}
                  alt=""
                  className="h-6 w-6 object-contain"
                />
              </span>
              <div className="min-w-0 text-xs text-slate-500">
                {icon
                  ? "Using custom uploaded icon"
                  : "Using preset platform icon"}
              </div>
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">
              Choose preset icon
            </p>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {SOCIAL_PLATFORM_OPTIONS.filter((o) => o.value !== "custom").map(
                (option) => {
                  const selected = platform === option.value && !icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      disabled={busy || uploadBusy}
                      onClick={() => handlePlatformChange(option.value)}
                      className={`flex flex-col items-center gap-2 rounded-xl border px-2 py-3 text-xs transition ${
                        selected
                          ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <img
                        src={SOCIAL_PLATFORM_ICONS[option.value]}
                        alt=""
                        className="h-5 w-5 object-contain"
                      />
                      {option.label}
                    </button>
                  );
                }
              )}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">
              Or upload custom icon
            </p>
            {icon ? (
              <div className="mb-2 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="flex min-w-0 items-center gap-2">
                  <img
                    src={resolveMediaUrl(icon) || icon}
                    alt=""
                    className="h-6 w-6 object-contain"
                  />
                  <span className="truncate text-xs text-slate-600">{icon}</span>
                </div>
                <button
                  type="button"
                  disabled={busy || uploadBusy}
                  onClick={() => setIcon("")}
                  className="shrink-0 text-xs font-medium text-red-600"
                >
                  Remove
                </button>
              </div>
            ) : null}
            <MediaUploadBox
              mediaType="image"
              busy={busy || uploadBusy}
              onUpload={async (file) => {
                if (!onUploadIcon) return;
                const uploadedUrl = await onUploadIcon(file);
                if (uploadedUrl) {
                  setIcon(uploadedUrl);
                  // Keep a known platform if already set; only mark custom when unknown.
                  setPlatform((prev) =>
                    SOCIAL_PLATFORM_ICONS[prev] ? prev : "custom"
                  );
                }
              }}
            />
          </div>

          <label className="block text-sm">
            <span className="font-medium text-slate-700">Label</span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={busy || uploadBusy}
              placeholder="LinkedIn"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            />
          </label>

          <label className="block text-sm">
            <span className="font-medium text-slate-700">URL</span>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={busy || uploadBusy}
              placeholder="https://linkedin.com/company/..."
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            />
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              disabled={busy || uploadBusy}
              onClick={onClose}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || uploadBusy}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {busy ? "Saving…" : initial ? "Save changes" : "Add link"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
